import type { JSONContent } from '@tiptap/core'
import type { WireInlineBlock } from '../../../api/types'

/** WireInlineBlock[] → a single-paragraph Tiptap doc JSON */
export function inlineToTiptap(children: WireInlineBlock[]): JSONContent {
  const content: JSONContent[] = children.flatMap((node): JSONContent[] => {
    if (node.type === 'span') {
      if (!node.text) return []
      const marks: NonNullable<JSONContent['marks']> = []
      if (node.typography?.includes('text-bold')) marks.push({ type: 'bold' })
      if (node.typography?.includes('text-italic')) marks.push({ type: 'italic' })
      return [{ type: 'text', text: node.text, ...(marks.length && { marks }) }]
    }
    if (node.type === 'link') {
      return [{
        type: 'text',
        text: node.text,
        marks: [{ type: 'link', attrs: { href: node.url, target: '_blank', rel: 'noopener noreferrer' } }],
      }]
    }
    if (node.type === 'citation') {
      return [{ type: 'citation', attrs: { sourceId: node.sourceId } }]
    }
    return []
  })

  return {
    type: 'doc',
    content: [{ type: 'paragraph', content: content.length ? content : undefined }],
  }
}

/** Single-paragraph Tiptap doc JSON → WireInlineBlock[] */
export function tiptapToInline(doc: JSONContent): WireInlineBlock[] {
  const para = doc.content?.[0]
  if (!para?.content) return []

  const result: WireInlineBlock[] = []

  for (const node of para.content) {
    if (node.type === 'text' && node.text) {
      const linkMark = node.marks?.find(m => m.type === 'link')
      if (linkMark) {
        result.push({ type: 'link', url: linkMark.attrs?.href ?? '', text: node.text })
        continue
      }
      const typography: string[] = []
      if (node.marks?.find(m => m.type === 'bold')) typography.push('text-bold')
      if (node.marks?.find(m => m.type === 'italic')) typography.push('text-italic')
      result.push({ type: 'span', text: node.text, ...(typography.length && { typography }) })
    } else if (node.type === 'citation') {
      result.push({ type: 'citation', sourceId: node.attrs?.sourceId ?? '' })
    }
  }

  return result
}
